import { useEffect } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import AgeValidationModal, { useAgeValidationModal } from 'features/shop/AgeValidationModal';
import ProductCardV2 from 'features/shop/ProductCardV2';
import { Alert } from 'honeydew/Alert';
import Modal from 'honeydew/Modal';
import useGlobalStore from 'hooks/useGlobalStore';
import logError from 'utils/errorUtils';

/**
 * Modal component to display all items in a carousel
 *
 * @param {Object} props Component props
 * @param {Object} props.data Carousel data containing items to display
 * @param {string} [props.deepLinkProductID] Product ID to scroll to when modal opens
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {Function} props.onClose Function to call when modal is closed
 * @param {string} [props.dsSourceOverride] Data source override for ProductCardV2
 * @param {Object} [props.productCardMetaData] Metadata object for ProductCardV2
 * @param {string} [props.productCardMetaData.carouselID] Carousel ID for ProductCardV2 metadata
 * @param {string} [props.productCardMetaData.carouselName] Carousel name for ProductCardV2 metadata
 * @param {string} [props.productCardMetaData.carouselType] Carousel type for ProductCardV2 metadata
 * @returns {JSX.Element} ProductsModal component
 */
function ProductsModal({
    data = null,
    deepLinkProductID,
    dsSourceOverride,
    isOpen = false,
    onClose = () => {},
    productCardMetaData = {},
}) {
    const { description, items = [], name, variant } = data ?? { name: '', description: '', items: [] };

    const { productsModalDataRef } = useGlobalStore();

    const { data: account = {} } = useAccountQuery();
    const { data: subscription = {} } = useSubscriptionQuery();
    const { isAgeValidationModalOpen, setIsAgeValidationModalOpen } = useAgeValidationModal({ subscription });

    /**
     * Scrolls to a specific product when the modal opens
     * Uses either deepLinkProductID or productsModalDataRef to find the target product
     */
    useEffect(() => {
        let timeoutId;

        const productID = deepLinkProductID || productsModalDataRef.current?.productID || null;

        if (isOpen && productID) {
            timeoutId = setTimeout(() => {
                const modalElement = document.getElementById('products-modal');
                const productEl = modalElement?.querySelector(`[data-product-id="${productID}"]`);

                if (productsModalDataRef.current) {
                    productsModalDataRef.current.productID = null;
                    productsModalDataRef.current.carouselID = null;
                }

                if (productEl) {
                    productEl.scrollIntoView({ behavior: 'smooth' });
                }
            }, 500);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [deepLinkProductID, isOpen]);

    /**
     * Logs error to Sentry when product collection fails to load
     */
    useEffect(() => {
        if (isOpen && (!items || items.length === 0)) {
            logError(new Error('No products to display in ProductsModal'), { carouselName: name || 'unknown' });
        }
    }, [isOpen, items, name]);

    // Special case: display recurring toggle for Order Again carousel
    const enableRecurringToggle = name === 'Order Again';

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            headline={
                <>
                    {name && <h2 className="text-body-lg mb-10 font-grotesk-bold">{name}</h2>}
                    {description && <p className="text-body-sm mb-10 text-grey">{description}</p>}
                </>
            }
            id="products-modal"
            styles={{
                contentRoot: 'pb-0',
            }}
        >
            {items?.length > 0 ? (
                <>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(145px,1fr))] gap-10 pb-20 md:grid-cols-4 md:gap-15">
                        {items.map((item) => (
                            <ProductCardV2
                                key={item.id}
                                data={item}
                                dsSourceOverride={dsSourceOverride ?? null}
                                layout="vertical"
                                metaData={{
                                    ...productCardMetaData,
                                    isCarouselModalCard: true,
                                }}
                                setModalOpen={setIsAgeValidationModalOpen}
                                enableRecurringToggle={enableRecurringToggle}
                                lazyLoadImage={false}
                                variant={variant}
                            />
                        ))}
                    </div>
                    <AgeValidationModal
                        modalOpen={isAgeValidationModalOpen}
                        setModalOpen={setIsAgeValidationModalOpen}
                        birthday={subscription?.dateOfBirth}
                        subscriptionID={subscription?.id}
                        customerID={account?.customerID}
                    />
                </>
            ) : (
                <div className="mb-20">
                    <Alert variant="error">
                        We&apos;re sorry, but it looks like there was a problem loading products.
                    </Alert>
                </div>
            )}
        </Modal>
    );
}

export default ProductsModal;
